import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getFairPlayGame,
  getFairPlayQueue,
  runFairPlayEngineReview,
  addFairPlayNote,
  upsertFairPlayCase,
  createPlayBan,
  liftPlayBan,
  listPlayBans,
  listOpenChessReports,
  resolveChessReport,
  listOpenChessAppeals,
  resolveChessAppeal,
  getChessChat,
} from '../../api/playing';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import Button from '../../components/common/Button';
import UserLink from '../../components/common/UserLink';
import { thinkTimeBars } from '../../playing/gameAnalysis';
import { PlayingBanner, PlayingEmptyState, PlayingSkeleton, PlayingStatusChip, formatReportReason } from './playingUi';
import { formatDateTime } from '../../utils/regionalFormat';
import { apiErrorMessage } from '../../utils/apiError';

function SideCard({ side, t }) {
  if (!side) return null;
  return (
    <div className="playing-fairplay-side rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="font-semibold text-slate-900">
          <UserLink
            id={side.userId}
            name={side.displayName}
            className="text-slate-900 hover:text-primary-700"
          />
        </p>
        <PlayingStatusChip status={side.level} label={t(`playing.fairPlayLevel.${side.level}`, { defaultValue: side.level })} />
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm text-slate-600">
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('playing.fairPlayMedian')}</dt>
          <dd className="tabular-nums font-semibold text-slate-800">
            {side.medianThinkMs != null ? `${side.medianThinkMs} ms` : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('playing.fairPlayFastMoves')}</dt>
          <dd className="tabular-nums font-semibold text-slate-800">{side.fastMoveCount}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('playing.fairPlayStreak')}</dt>
          <dd className="tabular-nums font-semibold text-slate-800">{side.longestFastStreak}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('playing.moves')}</dt>
          <dd className="tabular-nums font-semibold text-slate-800">{side.moveCount}</dd>
        </div>
        {side.engineMatchTop1Pct != null && (
          <>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('playing.engineMatchTop1')}</dt>
              <dd className="tabular-nums font-semibold text-indigo-700">{side.engineMatchTop1Pct}%</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('playing.engineMatchTop3')}</dt>
              <dd className="tabular-nums font-semibold text-indigo-700">{side.engineMatchTop3Pct}%</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('playing.avgCpLoss')}</dt>
              <dd className="tabular-nums font-semibold text-slate-800">{side.avgCpLoss}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('playing.engineLevel')}</dt>
              <dd>
                <PlayingStatusChip
                  status={side.engineLevel || 'LOW'}
                  label={t(`playing.fairPlayLevel.${side.engineLevel || 'LOW'}`, { defaultValue: side.engineLevel })}
                />
              </dd>
            </div>
          </>
        )}
      </dl>
    </div>
  );
}

function ThinkSpark({ moves }) {
  const bars = useMemo(() => thinkTimeBars(moves), [moves]);
  if (!bars.length) return null;
  return (
    <div className="playing-think-spark" aria-hidden>
      {bars.map((b) => (
        <span
          key={b.ply}
          className={`playing-think-spark-bar ${b.fast ? 'playing-think-spark-bar--fast' : ''}`}
          style={{ height: `${Math.max(8, Math.round(b.ratio * 100))}%` }}
          title={b.ms != null ? `#${b.ply}: ${b.ms}ms` : `#${b.ply}`}
        />
      ))}
    </div>
  );
}

function reviewStatusBanner(status, t) {
  if (status === 'PENDING') return { tone: 'warning', text: t('playing.engineReviewAutoPending') };
  if (status === 'RUNNING') return { tone: 'warning', text: t('playing.engineReviewAutoRunning') };
  if (status === 'DONE') return { tone: 'success', text: t('playing.engineReviewAutoDone') };
  if (status === 'FAILED') return { tone: 'warning', text: t('playing.engineReviewAutoFailed') };
  return null;
}

export default function FairPlayReviewPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const parseMinLevel = (raw) => {
    const v = String(raw || '').toUpperCase();
    return ['LOW', 'WATCH', 'HIGH'].includes(v) ? v : 'WATCH';
  };
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [minLevel, setMinLevel] = useState(() => parseMinLevel(searchParams.get('minLevel')));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [engineBusy, setEngineBusy] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [subjectUserId, setSubjectUserId] = useState('');
  const [caseStatus, setCaseStatus] = useState('OPEN');
  const [caseResolution, setCaseResolution] = useState('NONE');
  const [banReason, setBanReason] = useState('');
  const [banDays, setBanDays] = useState('');
  const [modBusy, setModBusy] = useState(false);
  const [activeBans, setActiveBans] = useState([]);
  const [bansLoadError, setBansLoadError] = useState(false);
  const [openReports, setOpenReports] = useState([]);
  const [openAppeals, setOpenAppeals] = useState([]);
  const [moderationExtraError, setModerationExtraError] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatError, setChatError] = useState('');
  const [rejectAppealId, setRejectAppealId] = useState(null);
  const [rejectAppealNote, setRejectAppealNote] = useState('');
  const [rejectAppealBusy, setRejectAppealBusy] = useState(false);
  const [confirmBanOpen, setConfirmBanOpen] = useState(false);
  const [confirmLiftBanId, setConfirmLiftBanId] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const changeMinLevel = (next) => {
    setMinLevel(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'WATCH') params.delete('minLevel');
    else params.set('minLevel', next);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    const fromUrl = parseMinLevel(searchParams.get('minLevel'));
    setMinLevel((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    setError('');
    setDetail(null);
    setChatMessages([]);
    setChatError('');
    setModerationExtraError(false);
    let cancelled = false;
    (async () => {
      try {
        if (id) {
          const d = await getFairPlayGame(id);
          if (!cancelled) setDetail(d);
          try {
            const chat = await getChessChat(id);
            if (!cancelled) setChatMessages(Array.isArray(chat) ? chat : []);
          } catch {
            if (!cancelled) {
              setChatMessages([]);
              setChatError(t('playing.chatLoadFailed', { defaultValue: 'Could not load game chat.' }));
            }
          }
        } else {
          const [q, bans, reports, appeals] = await Promise.all([
            getFairPlayQueue({ minLevel, limit: 50 }),
            listPlayBans({ activeOnly: true }).then((rows) => {
              setBansLoadError(false);
              return rows;
            }).catch(() => {
              setBansLoadError(true);
              return [];
            }),
            listOpenChessReports().catch(() => { setModerationExtraError(true); return []; }),
            listOpenChessAppeals().catch(() => { setModerationExtraError(true); return []; }),
          ]);
          if (!cancelled) {
            setRows(q);
            setActiveBans(Array.isArray(bans) ? bans : []);
            setOpenReports(Array.isArray(reports) ? reports : []);
            setOpenAppeals(Array.isArray(appeals) ? appeals : []);
          }
        }
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e, t('playing.loadFailed')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, minLevel, t, reloadTick]);

  useEffect(() => {
    if (!detail?.summary) return;
    const s = detail.summary;
    setSubjectUserId(String(s.whiteUserId || s.blackUserId || ''));
    setCaseStatus('OPEN');
    setCaseResolution('NONE');
    setBanReason('');
    setBanDays('');
    setNoteBody('');
  }, [id, detail?.summary?.gameId]);

  // Poll while automatic anti-cheat is still running
  useEffect(() => {
    if (!id || !detail) return undefined;
    const st = detail.reviewStatus || detail.summary?.reviewStatus;
    if (st !== 'PENDING' && st !== 'RUNNING') return undefined;
    const timer = setInterval(async () => {
      try {
        const d = await getFairPlayGame(id);
        setDetail(d);
      } catch {
        /* keep previous */
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [id, detail?.reviewStatus, detail?.summary?.reviewStatus]);

  const onEngineReview = async () => {
    if (!id || engineBusy) return;
    setEngineBusy(true);
    setError('');
    try {
      setDetail(await runFairPlayEngineReview(id));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.engineReviewFailed')));
    } finally {
      setEngineBusy(false);
    }
  };

  const refreshDetail = async () => {
    if (!id) return;
    setDetail(await getFairPlayGame(id));
  };

  const onSaveNote = async () => {
    if (!id || modBusy || !noteBody.trim()) return;
    setModBusy(true);
    setError('');
    try {
      await addFairPlayNote(id, {
        body: noteBody.trim(),
        subjectUserId: subjectUserId ? Number(subjectUserId) : undefined,
      });
      setNoteBody('');
      await refreshDetail();
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.fairPlayNoteFailed')));
    } finally {
      setModBusy(false);
    }
  };

  const onSaveCase = async () => {
    if (!id || modBusy || !subjectUserId) return;
    setModBusy(true);
    setError('');
    try {
      await upsertFairPlayCase(id, {
        subjectUserId: Number(subjectUserId),
        status: caseStatus,
        resolution: caseResolution,
      });
      await refreshDetail();
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.fairPlayCaseFailed')));
    } finally {
      setModBusy(false);
    }
  };

  const onBan = async () => {
    if (!id || modBusy || !subjectUserId || !banReason.trim()) return;
    setConfirmBanOpen(true);
  };

  const confirmBan = async () => {
    if (!id || modBusy || !subjectUserId || !banReason.trim()) return;
    setConfirmBanOpen(false);
    setModBusy(true);
    setError('');
    try {
      const payload = {
        userId: Number(subjectUserId),
        reason: banReason.trim(),
        gameId: Number(id),
      };
      if (banDays !== '' && Number(banDays) > 0) payload.days = Number(banDays);
      await createPlayBan(payload);
      setBanReason('');
      await refreshDetail();
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.fairPlayBanFailed')));
    } finally {
      setModBusy(false);
    }
  };

  const onLiftBan = async (banId) => {
    if (!banId || modBusy) return;
    setConfirmLiftBanId(banId);
  };

  const confirmLiftBan = async () => {
    const banId = confirmLiftBanId;
    if (!banId || modBusy) return;
    setConfirmLiftBanId(null);
    setModBusy(true);
    setError('');
    try {
      await liftPlayBan(banId);
      if (id) {
        await refreshDetail();
      } else {
        try {
          setActiveBans(await listPlayBans({ activeOnly: true }));
          setBansLoadError(false);
        } catch {
          setActiveBans([]);
          setBansLoadError(true);
        }
      }
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.fairPlayBanFailed')));
    } finally {
      setModBusy(false);
    }
  };

  if (id) {
    if (loading) {
      return (
        <div className="page-shell-form playing-page max-w-4xl">
          <PageHeaderSection eyebrow={t('nav.playingPlatform')} title={t('playing.fairPlay')} subtitle={t('playing.loading')} />
          <div className="list-table-card p-4"><PlayingSkeleton rows={5} /></div>
        </div>
      );
    }
    const summary = detail?.summary;
    const moves = detail?.moves || [];
    const engineMoves = detail?.engineMoves || [];
    const byPlyEngine = Object.fromEntries(engineMoves.map((m) => [m.ply, m]));
    const showEngineCols = Boolean(detail?.engineReview)
      || moves.some((m) => m.matchRank != null || m.cpLoss != null)
      || engineMoves.some((m) => m.matchRank != null || m.cpLoss != null);
    const statusBanner = reviewStatusBanner(detail?.reviewStatus || summary?.reviewStatus, t);
    if (!summary) {
      return (
        <div className="page-shell-form playing-page max-w-4xl">
          <PageHeaderSection
            eyebrow={t('nav.playingPlatform')}
            title={t('playing.fairPlayReview')}
            subtitle={t('playing.fairPlay')}
          />
          {error && <PlayingBanner tone="danger">{error}</PlayingBanner>}
          {!error && (
            <PlayingBanner tone="warning">{t('playing.loadFailed')}</PlayingBanner>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="page-primary" onClick={() => setReloadTick((n) => n + 1)}>
              {t('playing.retry', { defaultValue: 'Retry' })}
            </Button>
            <Button variant="page-secondary" onClick={() => navigate('/playing/fair-play')}>
              {t('playing.fairPlay')}
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="page-shell-form playing-page max-w-4xl">
        <PageHeaderSection
          eyebrow={t('nav.playingPlatform')}
          title={t('playing.fairPlayReview')}
          subtitle={
            summary ? (
              <>
                <UserLink id={summary.whiteUserId} name={summary.whiteName} className="text-slate-600 hover:text-primary-700" />
                {' vs '}
                <UserLink id={summary.blackUserId} name={summary.blackName} className="text-slate-600 hover:text-primary-700" />
              </>
            ) : t('playing.fairPlay')
          }
          aside={summary && (
            <PlayingStatusChip
              status={summary.level}
              label={t(`playing.fairPlayLevel.${summary.level}`, { defaultValue: summary.level })}
            />
          )}
        />
        {error && <PlayingBanner tone="danger">{error}</PlayingBanner>}
        {statusBanner && <PlayingBanner tone={statusBanner.tone}>{statusBanner.text}</PlayingBanner>}
        {detail?.engineUnavailableReason && (
          <PlayingBanner tone="warning">
            {(() => {
              const reason = String(detail.engineUnavailableReason);
              if (reason === 'Stockfish not started') return t('playing.stockfish.notStarted');
              if (reason.startsWith('Stockfish disabled')) return t('playing.stockfish.disabled');
              if (reason.startsWith('Stockfish unavailable') || reason.startsWith('Stockfish evaluation failed')) {
                return t('playing.stockfish.unavailable');
              }
              return t('playing.stockfish.unavailable');
            })()}
          </PlayingBanner>
        )}
        <div className="mb-4 flex flex-wrap gap-2">
          <Button variant="page-secondary" onClick={() => navigate('/playing/fair-play')}>
            {t('playing.fairPlay')}
          </Button>
          {summary?.gameId && (
            <Link
              to={`/playing/spectate/${summary.gameId}?analyze=1`}
              className="playing-text-link font-semibold self-center"
            >
              {t('playing.openBoard')}
            </Link>
          )}
          <Button variant="page-primary" onClick={onEngineReview} disabled={engineBusy}>
            {engineBusy ? t('playing.engineReviewRunning') : t('playing.runEngineReview')}
          </Button>
        </div>
        {summary && (
          <div className="mb-5 grid gap-3 sm:grid-cols-2">
            <SideCard side={summary.white} t={t} />
            <SideCard side={summary.black} t={t} />
          </div>
        )}
        <section className="mb-5 list-table-card overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
            <h2 className="font-semibold text-slate-900">
              {t('playing.gameChat', { defaultValue: 'Game chat' })}
            </h2>
          </div>
          {chatError ? (
            <p className="px-4 py-3 text-sm text-amber-800">{chatError}</p>
          ) : chatMessages.length === 0 ? (
            <p className="px-4 py-3 text-sm text-slate-500">
              {t('playing.chatEmpty')}
            </p>
          ) : (
            <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto">
              {chatMessages.map((m) => (
                <li key={m.id} className="px-4 py-2 text-sm">
                  <span className="font-semibold text-slate-800">
                    <UserLink id={m.userId} name={m.userName || t('playing.player')} playing className="text-slate-800 hover:text-primary-700" />
                  </span>
                  <span className="ml-2 text-slate-700">{m.body}</span>
                  {m.createdAt && (
                    <span className="ml-2 text-xs text-slate-400">{formatDateTime(m.createdAt)}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="mb-5 list-table-card overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
            <h2 className="font-semibold text-slate-900">{t('playing.fairPlayModeration')}</h2>
          </div>
          <div className="space-y-4 px-4 py-4">
            {(detail?.playerBans || []).length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t('playing.fairPlayActiveBans')}
                </p>
                <ul className="space-y-2">
                  {detail.playerBans.map((b) => (
                    <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm">
                      <span>
                        <strong>
                          <UserLink
                            id={b.userId}
                            name={b.userName || String(b.userId)}
                            className="text-slate-900 hover:text-primary-700"
                          />
                        </strong>
                        {': '}
                        {b.reason}
                        {b.expiresAt ? ` · ${formatDateTime(b.expiresAt)}` : ''}
                      </span>
                      <Button variant="page-secondary" disabled={modBusy} onClick={() => onLiftBan(b.id)}>
                        {t('playing.fairPlayLiftBan')}
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(detail?.cases || []).length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t('playing.fairPlayCases')}
                </p>
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100">
                  {detail.cases.map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span>
                        <UserLink
                          id={c.subjectUserId}
                          name={c.subjectName}
                          className="text-slate-900 hover:text-primary-700"
                        />
                        {' · '}
                        {t(`playing.fairPlayCaseStatusLabel.${c.status}`, { defaultValue: c.status })}
                        {c.openedBy == null && (
                          <>
                            {' · '}
                            <span className="text-amber-700">{t('playing.fairPlayCaseAutoOpened')}</span>
                          </>
                        )}
                        {c.resolution && c.resolution !== 'NONE' && (
                          <>
                            {' · '}
                            {t(`playing.fairPlayResolutionLabel.${c.resolution}`, { defaultValue: c.resolution })}
                          </>
                        )}
                      </span>
                      <span className="text-xs text-slate-400">{c.updatedAt || c.openedAt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(detail?.notes || []).length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t('playing.fairPlayNotes')}
                </p>
                <ul className="space-y-2">
                  {detail.notes.map((n) => (
                    <li key={n.id} className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-sm">
                      <p className="text-slate-800 whitespace-pre-wrap">{n.body}</p>
                      <p className="mt-1 text-xs text-slate-400">
                        <UserLink
                          id={n.staffUserId}
                          name={n.staffName}
                          className="text-slate-500 hover:text-primary-700"
                        />
                        {n.createdAt ? ` · ${n.createdAt}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-medium text-slate-700">
                {t('playing.fairPlaySubject')}
                <select
                  className="select mt-1"
                  value={subjectUserId}
                  onChange={(e) => setSubjectUserId(e.target.value)}
                >
                  {summary?.whiteUserId && (
                    <option value={summary.whiteUserId}>{summary.whiteName}</option>
                  )}
                  {summary?.blackUserId && (
                    <option value={summary.blackUserId}>{summary.blackName}</option>
                  )}
                </select>
              </label>
              <label className="text-sm font-medium text-slate-700">
                {t('playing.fairPlayCaseStatus')}
                <select className="select mt-1" value={caseStatus} onChange={(e) => setCaseStatus(e.target.value)}>
                  {['OPEN', 'WATCHING', 'ACTIONED', 'CLOSED', 'DISMISSED'].map((s) => (
                    <option key={s} value={s}>
                      {t(`playing.fairPlayCaseStatusLabel.${s}`, { defaultValue: s })}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium text-slate-700">
                {t('playing.fairPlayResolution')}
                <select className="select mt-1" value={caseResolution} onChange={(e) => setCaseResolution(e.target.value)}>
                  {['NONE', 'WARNING', 'BAN', 'DISMISSED'].map((s) => (
                    <option key={s} value={s}>
                      {t(`playing.fairPlayResolutionLabel.${s}`, { defaultValue: s })}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-end">
                <Button variant="page-secondary" disabled={modBusy} onClick={onSaveCase}>
                  {t('playing.fairPlayOpenCase')}
                </Button>
              </div>
            </div>
            <label className="block text-sm font-medium text-slate-700">
              {t('playing.fairPlayAddNote')}
              <textarea
                className="input mt-1 min-h-[72px] w-full"
                value={noteBody}
                placeholder={t('playing.fairPlayNotePlaceholder')}
                onChange={(e) => setNoteBody(e.target.value)}
              />
            </label>
            <Button variant="page-primary" disabled={modBusy || !noteBody.trim()} onClick={onSaveNote}>
              {t('playing.fairPlaySaveNote')}
            </Button>
            <div className="grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2">
              <label className="text-sm font-medium text-slate-700">
                {t('playing.fairPlayBanReason')}
                <input
                  className="input mt-1 w-full"
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                />
              </label>
              <label className="text-sm font-medium text-slate-700">
                {t('playing.fairPlayBanDays')}
                <input
                  className="input mt-1 w-full"
                  type="number"
                  min="1"
                  value={banDays}
                  onChange={(e) => setBanDays(e.target.value)}
                />
              </label>
            </div>
            <Button variant="page-secondary" disabled={modBusy || !banReason.trim()} onClick={onBan}>
              {t('playing.fairPlayBanPlayer')}
            </Button>
          </div>
        </section>
        <section className="mb-5 list-table-card overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
            <h2 className="font-semibold text-slate-900">{t('playing.thinkSparkTitle')}</h2>
            <p className="text-xs text-slate-500">{t('playing.thinkSparkHint')}</p>
          </div>
          <div className="px-4 py-4">
            <ThinkSpark moves={moves} />
          </div>
        </section>
        <section className="list-table-card overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
            <h2 className="font-semibold text-slate-900">{t('playing.moveTiming')}</h2>
          </div>
          {moves.length === 0 ? (
            <PlayingEmptyState title={t('playing.noMovesYet')} />
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">{t('playing.moveNumber')}</th>
                  <th className="px-4 py-2 text-left">{t('playing.san')}</th>
                  <th className="px-4 py-2 text-right">{t('playing.thinkTime')}</th>
                  {showEngineCols && (
                    <>
                      <th className="px-4 py-2 text-right">{t('playing.cpLoss')}</th>
                      <th className="px-4 py-2 text-left">{t('playing.engineMatch')}</th>
                      <th className="px-4 py-2 text-left">{t('playing.bestMove')}</th>
                      <th className="px-4 py-2 text-left">{t('playing.engineFlag')}</th>
                    </>
                  )}
                  <th className="px-4 py-2 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {moves.map((m) => {
                  const eng = byPlyEngine[m.ply];
                  const matchRank = eng?.matchRank ?? m.matchRank;
                  const cpLoss = eng?.cpLoss ?? m.cpLoss;
                  const bestUci = eng?.bestUci ?? m.bestUci;
                  const engineFlag = eng?.engineFlag ?? m.engineFlag;
                  const suspicious =
                    engineFlag === 'HIGH' || engineFlag === 'WATCH'
                    || (m.thinkTimeMs != null && m.thinkTimeMs < 400 && m.ply > 8)
                    || (matchRank === 1 && cpLoss != null && cpLoss < 15 && (eng?.postOpening ?? m.ply > 8));
                  return (
                    <tr key={m.ply} className={suspicious ? 'bg-amber-50/80' : ''}>
                      <td className="px-4 py-2 tabular-nums text-slate-400">{m.ply}</td>
                      <td className="px-4 py-2 font-medium text-slate-900">{m.san}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {m.thinkTimeMs != null ? `${m.thinkTimeMs} ms` : '—'}
                      </td>
                      {showEngineCols && (
                        <>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {cpLoss != null ? cpLoss : '—'}
                          </td>
                          <td className="px-4 py-2">
                            {matchRank != null ? `#${matchRank}` : '—'}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-slate-600">
                            {bestUci || '—'}
                          </td>
                          <td className="px-4 py-2">
                            {engineFlag ? (
                              <PlayingStatusChip
                                status={engineFlag}
                                label={t(`playing.fairPlayLevel.${engineFlag}`, { defaultValue: engineFlag })}
                              />
                            ) : '—'}
                          </td>
                        </>
                      )}
                      <td className="px-4 py-2 text-right">
                        {summary?.gameId && (
                          <Link
                            to={`/playing/spectate/${summary.gameId}?ply=${m.ply}&analyze=1`}
                            className="playing-text-link text-xs font-semibold"
                          >
                            {t('playing.jumpToPly')}
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

      {confirmBanOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          onClick={() => { if (!modBusy) setConfirmBanOpen(false); }}
        >
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-slate-900">{t('playing.banConfirm')}</h2>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="page-secondary" disabled={modBusy} onClick={() => setConfirmBanOpen(false)}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button variant="page-primary" disabled={modBusy} onClick={confirmBan}>
                {t('playing.createBan', { defaultValue: 'Ban player' })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmLiftBanId != null && (
        <div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          onClick={() => { if (!modBusy) setConfirmLiftBanId(null); }}
        >
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-slate-900">{t('playing.liftBanConfirm')}</h2>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="page-secondary" disabled={modBusy} onClick={() => setConfirmLiftBanId(null)}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button variant="page-primary" disabled={modBusy} onClick={confirmLiftBan}>
                {t('playing.liftBan', { defaultValue: 'Lift ban' })}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    );
  }

  return (
    <div className="page-shell-form playing-page max-w-3xl">
      <PageHeaderSection
        eyebrow={t('nav.playingPlatform')}
        title={t('playing.fairPlay')}
        subtitle={t('playing.fairPlaySubtitle')}
      />
      {error && <PlayingBanner tone="danger">{error}</PlayingBanner>}
      {moderationExtraError && (
        <PlayingBanner tone="warning">
          {t('playing.reportsAppealsLoadFailed', {
            defaultValue: 'Could not load open reports or appeals.',
          })}
        </PlayingBanner>
      )}
      <div className="mb-4">
        <label className="text-sm font-medium text-slate-700">
          {t('playing.fairPlayMinLevel')}
          <select className="select mt-1" value={minLevel} onChange={(e) => changeMinLevel(e.target.value)}>
            <option value="LOW">{t('playing.fairPlayLevel.LOW')}</option>
            <option value="WATCH">{t('playing.fairPlayLevel.WATCH')}</option>
            <option value="HIGH">{t('playing.fairPlayLevel.HIGH')}</option>
          </select>
        </label>
      </div>

      <section className="list-table-card mb-6 overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
          <h2 className="font-semibold text-slate-900">
            {t('playing.openReports', { defaultValue: 'Open player reports' })}
            {openReports.length > 0 ? ` · ${openReports.length}` : ''}
          </h2>
        </div>
        {openReports.length === 0 ? (
          moderationExtraError ? null : (
          <p className="px-4 py-3 text-sm text-slate-500">
            {t('playing.noOpenReports', { defaultValue: 'No open reports.' })}
          </p>
          )
        ) : (
          <ul className="divide-y divide-slate-100">
            {openReports.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">
                    <UserLink id={r.reportedUserId} name={r.reportedName} playing className="text-slate-900 hover:text-primary-700" />
                    <span className="mx-1 text-slate-400">·</span>
                    <span className="text-xs font-medium uppercase text-slate-500">
                      {formatReportReason(r.reason, t)}
                    </span>
                  </p>
                  <p className="text-sm text-slate-600">{r.details || '—'}</p>
                  <p className="text-xs text-slate-400">
                    {t('playing.reportedBy', { defaultValue: 'Reported by' })}{' '}
                    <UserLink id={r.reporterUserId} name={r.reporterName} playing className="text-slate-600 hover:text-primary-700" />
                    {r.gameId ? ` · #${r.gameId}` : ''}
                    {r.createdAt ? ` · ${formatDateTime(r.createdAt)}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {r.gameId && (
                    <Button variant="page-secondary" onClick={() => navigate(`/playing/fair-play/${r.gameId}`)}>
                      {t('playing.open')}
                    </Button>
                  )}
                  <Button
                    variant="page-secondary"
                    disabled={modBusy}
                    onClick={async () => {
                      setModBusy(true);
                      try {
                        await resolveChessReport(r.id, 'DISMISSED');
                        setOpenReports((prev) => prev.filter((x) => Number(x.id) !== Number(r.id)));
                      } catch (e) {
                        setError(apiErrorMessage(e, t('playing.fairPlayReportFailed', {
                          defaultValue: 'Could not update this report.',
                        })));
                      } finally {
                        setModBusy(false);
                      }
                    }}
                  >
                    {t('playing.dismissReport', { defaultValue: 'Dismiss' })}
                  </Button>
                  <Button
                    variant="page-primary"
                    disabled={modBusy}
                    onClick={async () => {
                      setModBusy(true);
                      try {
                        await resolveChessReport(r.id, 'RESOLVED');
                        setOpenReports((prev) => prev.filter((x) => Number(x.id) !== Number(r.id)));
                        if (r.gameId) navigate(`/playing/fair-play/${r.gameId}`);
                      } catch (e) {
                        setError(apiErrorMessage(e, t('playing.fairPlayReportFailed', {
                          defaultValue: 'Could not update this report.',
                        })));
                      } finally {
                        setModBusy(false);
                      }
                    }}
                  >
                    {t('playing.resolveReport', { defaultValue: 'Resolve' })}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="list-table-card mb-6 overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
          <h2 className="font-semibold text-slate-900">
            {t('playing.openAppeals', { defaultValue: 'Open ban appeals' })}
            {openAppeals.length > 0 ? ` · ${openAppeals.length}` : ''}
          </h2>
        </div>
        {openAppeals.length === 0 ? (
          moderationExtraError ? null : (
          <p className="px-4 py-3 text-sm text-slate-500">
            {t('playing.noOpenAppeals', { defaultValue: 'No open appeals.' })}
          </p>
          )
        ) : (
          <ul className="divide-y divide-slate-100">
            {openAppeals.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">
                    <UserLink id={a.userId} name={a.userName} playing className="text-slate-900 hover:text-primary-700" />
                  </p>
                  <p className="text-sm text-slate-600">{a.message}</p>
                  {a.banReason && (
                    <p className="text-xs text-slate-400">
                      {t('playing.banBannerReason', { reason: a.banReason })}
                    </p>
                  )}
                  {a.createdAt && (
                    <p className="text-xs text-slate-400">{formatDateTime(a.createdAt)}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="page-secondary"
                    disabled={modBusy || rejectAppealBusy}
                    onClick={() => {
                      setRejectAppealId(a.id);
                      setRejectAppealNote('');
                    }}
                  >
                    {t('playing.rejectAppeal', { defaultValue: 'Reject' })}
                  </Button>
                  <Button
                    variant="page-primary"
                    disabled={modBusy}
                    onClick={async () => {
                      setModBusy(true);
                      try {
                        await resolveChessAppeal(a.id, { status: 'ACCEPTED' });
                        setOpenAppeals((prev) => prev.filter((x) => Number(x.id) !== Number(a.id)));
                        try {
                          setActiveBans(await listPlayBans({ activeOnly: true }));
                        } catch {
                          /* ignore */
                        }
                      } catch (e) {
                        setError(apiErrorMessage(e, t('playing.fairPlayAppealFailed', {
                          defaultValue: 'Could not update this appeal.',
                        })));
                      } finally {
                        setModBusy(false);
                      }
                    }}
                  >
                    {t('playing.acceptAppeal', { defaultValue: 'Accept & lift' })}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="list-table-card mb-6 overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
          <h2 className="font-semibold text-slate-900">{t('playing.fairPlayActiveBans')}</h2>
        </div>
        {bansLoadError ? (
          <p className="px-4 py-3 text-sm text-rose-700" role="status">{t('common.listLoadFailed')}</p>
        ) : activeBans.length === 0 ? (
          <p className="px-4 py-3 text-sm text-slate-500">{t('playing.noActiveBans')}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {activeBans.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">
                    <UserLink
                      id={b.userId}
                      name={b.userName || String(b.userId)}
                      className="text-slate-900 hover:text-primary-700"
                    />
                  </p>
                  <p className="text-sm text-slate-600">{b.reason}</p>
                  <p className="text-xs text-slate-400">
                    {b.expiresAt
                      ? t('playing.banBannerUntil', { when: formatDateTime(b.expiresAt) })
                      : t('playing.banBannerIndefinite')}
                  </p>
                </div>
                <Button variant="page-secondary" disabled={modBusy} onClick={() => onLiftBan(b.id)}>
                  {t('playing.fairPlayLiftBan')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
      {loading ? (
        <div className="list-table-card p-4"><PlayingSkeleton rows={5} /></div>
      ) : rows.length === 0 && !error ? (
        <div className="list-table-card">
          <PlayingEmptyState
            title={t('playing.fairPlayEmpty')}
            hint={t('playing.fairPlayEmptyHint')}
            action={(
              <Button variant="page-secondary" onClick={() => changeMinLevel('LOW')}>
                {t('playing.fairPlayShowAllLevels', { defaultValue: 'Show all levels' })}
              </Button>
            )}
          />
        </div>
      ) : rows.length === 0 ? null : (
        <ul className="list-table-card divide-y divide-slate-100 overflow-hidden">
          {rows.map((r) => (
            <li key={r.gameId} className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50/80">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900">
                  <UserLink id={r.whiteUserId} name={r.whiteName} className="text-slate-900 hover:text-primary-700" />
                  {' vs '}
                  <UserLink id={r.blackUserId} name={r.blackName} className="text-slate-900 hover:text-primary-700" />
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {t(`playing.fairPlayLevel.${r.level}`, { defaultValue: r.level })}
                  {(() => {
                    const review = reviewStatusBanner(r.reviewStatus, t);
                    return review?.text ? (
                      <>
                        {' · '}
                        {review.text}
                      </>
                    ) : null;
                  })()}
                  {r.white?.fastMoveCount != null && (
                    <>
                      {' · '}
                      {t('playing.fairPlayFastSummary', {
                        white: r.white.fastMoveCount,
                        black: r.black?.fastMoveCount ?? 0,
                      })}
                    </>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <PlayingStatusChip
                  status={r.level}
                  label={t(`playing.fairPlayLevel.${r.level}`, { defaultValue: r.level })}
                />
                <Link to={`/playing/fair-play/${r.gameId}`} className="playing-text-link font-semibold">
                  {t('playing.review')}
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}

      {rejectAppealId != null && (
        <div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="appeal-reject-title"
          onClick={() => { if (!rejectAppealBusy) setRejectAppealId(null); }}
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="appeal-reject-title" className="text-lg font-semibold text-slate-900">
              {t('playing.rejectAppeal', { defaultValue: 'Reject appeal' })}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {t('playing.appealRejectNotePrompt', {
                defaultValue: 'Optional note to the player (why rejected):',
              })}
            </p>
            <textarea
              className="input mt-3"
              rows={3}
              maxLength={500}
              value={rejectAppealNote}
              onChange={(e) => setRejectAppealNote(e.target.value)}
              disabled={rejectAppealBusy}
            />
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button
                variant="page-secondary"
                disabled={rejectAppealBusy}
                onClick={() => setRejectAppealId(null)}
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                variant="page-primary"
                disabled={rejectAppealBusy}
                onClick={async () => {
                  setRejectAppealBusy(true);
                  try {
                    await resolveChessAppeal(rejectAppealId, {
                      status: 'REJECTED',
                      staffNote: rejectAppealNote || '',
                    });
                    setOpenAppeals((prev) => prev.filter((x) => Number(x.id) !== Number(rejectAppealId)));
                    setRejectAppealId(null);
                  } catch (e) {
                    setError(apiErrorMessage(e, t('playing.fairPlayAppealFailed', {
                      defaultValue: 'Could not update this appeal.',
                    })));
                  } finally {
                    setRejectAppealBusy(false);
                  }
                }}
              >
                {rejectAppealBusy
                  ? t('playing.submitting', { defaultValue: 'Submitting…' })
                  : t('playing.rejectAppeal', { defaultValue: 'Reject' })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmBanOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          onClick={() => { if (!modBusy) setConfirmBanOpen(false); }}
        >
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-slate-900">{t('playing.banConfirm')}</h2>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="page-secondary" disabled={modBusy} onClick={() => setConfirmBanOpen(false)}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button variant="page-primary" disabled={modBusy} onClick={confirmBan}>
                {t('playing.createBan', { defaultValue: 'Ban player' })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmLiftBanId != null && (
        <div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          onClick={() => { if (!modBusy) setConfirmLiftBanId(null); }}
        >
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-slate-900">{t('playing.liftBanConfirm')}</h2>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="page-secondary" disabled={modBusy} onClick={() => setConfirmLiftBanId(null)}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button variant="page-primary" disabled={modBusy} onClick={confirmLiftBan}>
                {t('playing.liftBan', { defaultValue: 'Lift ban' })}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
